import React from "react";
import { makeStyles } from "tss-react/mui";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Theme } from "@mui/material/styles";
import FormHelperText from "@mui/material/FormHelperText";
import InputLabel from "@mui/material/InputLabel";
import Skeleton from "@mui/material/Skeleton";
import Tooltip from "@mui/material/Tooltip";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup, { toggleButtonGroupClasses } from "@mui/material/ToggleButtonGroup";
import Stack, { StackProps } from "@mui/material/Stack";

const useStyles = makeStyles()((theme) => ({
  toggleButtonGroup: {
    gap: theme.spacing(2),
    display: "flex",
    flexWrap: "wrap",
    [`& .${toggleButtonGroupClasses.firstButton}, & .${toggleButtonGroupClasses.middleButton}`]:
      {
        borderTopRightRadius: theme.shape.borderRadius,
        borderBottomRightRadius: theme.shape.borderRadius,
      },
    [`& .${toggleButtonGroupClasses.lastButton}, & .${toggleButtonGroupClasses.middleButton}`]:
      {
        borderTopLeftRadius: theme.shape.borderRadius,
        borderBottomLeftRadius: theme.shape.borderRadius,
        borderLeft: `1px solid ${theme.palette.divider}`,
      }
  },
}));

export enum PlaceholderOptions {
  Null = "null",
  False = "false",
  True = "true",
}

interface BaseOptionSwitchInputProps<T> extends Omit<StackProps, "onChange"> {
  options: { value: T; label: React.ReactNode; helpText?: string; tooltip?: string }[];
  value: T | null | undefined;
  label?: string;
  helpText?: string;
  loading?: boolean;
  subButton?: {
    label: string;
    startIcon?: React.ReactNode;
    onClick: () => void;
  };
  readOnly?: boolean;
  buttonOverrideText?: string;
}

interface NoDeselectProps<T> extends BaseOptionSwitchInputProps<T> {
  allowDeselect?: false | undefined;
  onChange: (newValue: T) => void;
}

interface AllowDeselectProps<T> extends BaseOptionSwitchInputProps<T> {
  allowDeselect: true;
  onChange: (newValue: T | null) => void;
}

type OptionSwitchInputProps<T extends string | number> = NoDeselectProps<T> | AllowDeselectProps<T>;

function OptionSwitchInput<T extends string | number>({
  options,
  label,
  value,
  helpText,
  onChange,
  sx,
  loading,
  subButton,
  allowDeselect,
  buttonOverrideText,
  readOnly,
  ...props
}: OptionSwitchInputProps<T>) {

  const { classes } = useStyles();
  const matchesSmUp = useMediaQuery((theme: Theme) => theme.breakpoints.up("sm"));

  const changeHandler = (newValue: T | null) => {
    if (newValue == null && !allowDeselect) return;
    onChange(newValue as any);
  };

  return (
    <React.Fragment>
      <Stack spacing={0.5}>
        {label != null && <InputLabel>{loading ? <Skeleton /> : label}</InputLabel>}
        <Stack direction="row" alignItems="center">
          {loading ? (
            <Skeleton variant="rectangular" sx={(theme) => ({ width: "100%", height: theme.spacing(5) })} />
          ) : buttonOverrideText == null ? (
            <ToggleButtonGroup
              value={value}
              exclusive
              onChange={(_, newValue) => changeHandler(newValue)}
              size="small"
              className={classes.toggleButtonGroup}
            >
              {options.map((o) => (
                <Tooltip title={o.tooltip} key={o.value}>
                  <ToggleButton
                    disabled={readOnly}
                    value={o.value}
                    sx={{
                      px: 2,
                      flexDirection: "column",
                      fontSize: matchesSmUp ? "0.87rem" : "small",
                      fontWeight: "bold",
                      color: value === o.value ? "#118FF3CC !important" : "GrayText",
                      backgroundColor: value === o.value ? undefined : "#FFF",
                    }}
                  >
                    {o.label}
                    <Typography variant="caption" color="text.secondary">
                      {o.helpText}
                    </Typography>
                  </ToggleButton>
                </Tooltip>
              ))}
            </ToggleButtonGroup>
          ) : (
            <Typography>{buttonOverrideText}</Typography>
          )}
        </Stack>
        {subButton != null &&
          (loading ? (
            <Skeleton />
          ) : (
            <Button
              onClick={subButton.onClick}
              variant="text"
              size="small"
              startIcon={subButton.startIcon}
              sx={{ width: "max-content", maxWidth: "100%" }}
            >
              {subButton.label}
            </Button>
          ))}
        {helpText != null && <FormHelperText>{loading ? <Skeleton /> : helpText}</FormHelperText>}
      </Stack>
    </React.Fragment>
  );
};

export default OptionSwitchInput;
